import React, { useState, useRef, useEffect } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

export default function OptimizedImage({
  src,
  alt,
  className = '',
  placeholder,
  onError
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Reset states when src changes
    setIsLoaded(false);
    setHasError(false);
    setShouldLoad(false);

    // If no src provided, show error state immediately
    if (!src || src.trim() === '') {
      console.warn('OptimizedImage: No src provided');
      setHasError(true);
      return;
    }

    console.log('OptimizedImage: Setting up for src:', src);

    // Use Intersection Observer for lazy loading
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window && containerRef.current) {
      // Small delay to ensure container is mounted
      const timer = setTimeout(() => {
        if (containerRef.current) {
          observerRef.current = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !shouldLoad) {
                  console.log('OptimizedImage: Image is intersecting, starting load');
                  setShouldLoad(true);
                  observerRef.current?.disconnect();
                }
              });
            },
            {
              rootMargin: '50px' // Start loading 50px before image enters viewport
            }
          );

          observerRef.current.observe(containerRef.current);
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    } else {
      // Fallback: load immediately
      console.log('OptimizedImage: IntersectionObserver not available, loading immediately');
      setShouldLoad(true);
    }
  }, [src]);

  const handleLoad = () => {
    console.log('Image loaded successfully:', src);
    setIsLoaded(true);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Image failed to load:', src, e);
    setHasError(true);
    setIsLoaded(false);
    if (onError) {
      onError(e);
    }
  };

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* Placeholder/Loading state */}
      {!isLoaded && !hasError && shouldLoad && (
        <div className="absolute inset-0 bg-amber-100 dark:bg-amber-900 flex items-center justify-center z-10">
          <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Loading state before intersection */}
      {!shouldLoad && !hasError && (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900 dark:to-amber-800 flex items-center justify-center z-10">
          <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900 dark:to-amber-800 flex items-center justify-center z-20">
          <div className="text-center">
            <span className="text-5xl block mb-2">☕</span>
            <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">Coffee Shop</span>
          </div>
        </div>
      )}

      {/* Actual image */}
      {shouldLoad && src && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
}
